import { XIcon } from 'lucide-react';
import { NumberInput } from '../ui/NumberInput';
import { Stepper } from '../ui/Stepper';
import { Toggle } from '../ui/Toggle';
import { btnIcon, inputCls } from '../ui/styles';
import { money } from '../../core/format/currency';
import { lineTotalCents, MAX_QTY } from '../../core/rules/kassa';
import { toCents } from '../../core/format/currency';
import { kindLabel } from './kassaLabels';
import type { DraftLine, SaleComposerControl } from './useSaleComposer';
type Props = {
  line: DraftLine;
  c: SaleComposerControl;
};

/** Eén regel op de bon: prijs (korting met reden), aantal bij producten, en "uit kuur" bij de kuurafspraak. */
export function SaleLineRow({
  line,
  c
}: Props) {
  const l = line.input;
  const fromKuur = l.kind === 'treatment' && Boolean(l.fromKuur);
  const giftcard = l.kind === 'giftcard';
  // Product van een merk zonder korting: de prijs ligt vast.
  const priceLocked = l.kind === 'product' && !c.discountAllowed(l);
  const discounted = !fromKuur && !giftcard && !priceLocked && l.priceCents < line.listPriceCents;
  const problem = c.touched ? c.lineProblems[line.key] : null;
  const kuurOnBon = c.lines.some(x => x.input.kind === 'kuur');
  const level = l.kind === 'product' ? c.salonLevel(l.productId) : null;
  const shortBy = l.kind === 'product' && level !== null ? l.qty - Math.max(0, level) : 0;
  return <li className="py-3.5" data-id="element-2475" data-mp="1ofl28hnymklg">
      <div className="flex items-start gap-3" data-id="element-2476" data-mp="2c4w11tlr24wy">
        <div className="min-w-0 flex-1" data-id="element-2477" data-mp="2c676dxuufkq6">
          <p className="text-[14px] text-ink" data-id="element-2478" data-mp="2h3yyarvhn6ud">{line.name}</p>
          <p className="text-[12px] text-muted" data-id="element-2479" data-mp="2h3gtv7yo9mbd">
            {kindLabel[l.kind]}
            {line.listPriceCents > 0 && !fromKuur && !giftcard && ` · prijs ${money(line.listPriceCents)}`}
          </p>
        </div>
        <p className="pt-0.5 text-[14px] tabular-nums text-ink" data-id="element-2480" data-mp="273zsh2qgdul7">{fromKuur ? <span className="text-muted" data-id="element-2481" data-mp="rnzgynhpub3n">uit kuur · {money(0)}</span> : money(lineTotalCents(l))}</p>
        <button type="button" onClick={() => c.removeLine(line.key)} className={btnIcon} aria-label={`${line.name} van de bon halen`} data-id="element-2482" data-mp="vrp376ax4gli">
          <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2483" data-mp="2yfdxppaorcvz" />
        </button>
      </div>

      {c.canFromKuur(line) && l.kind === 'treatment' && <div className="mt-2.5" data-id="element-2484" data-mp="2omgefgxvdjwb">
          <Toggle checked={fromKuur} onChange={on => c.setInput(line.key, {
        fromKuur: on
      })} label={c.kuurSession ? `Uit de kuur (sessie ${c.appointment?.session}, al betaald)` : 'Telt als sessie 1 van de kuur op deze bon'} description={kuurOnBon && !c.kuurSession ? 'Dan staat hij op €0 en begint de kuur vandaag.' : 'Uit: deze behandeling wordt los afgerekend.'} data-id="element-2485" data-mp="2olezv2hnaf9u" />
        </div>}

      {!fromKuur && <div className="mt-2.5 flex flex-wrap items-start gap-3" data-id="element-2486" data-mp="20qud47s6pk97">
          {priceLocked ? <div data-id="element-2487" data-mp="3vzein173irl">
              <span className="mb-1 block text-[12px] text-muted" data-id="element-2488" data-mp="3whi64q6sz9j">Prijs per stuk</span>
              <p className="py-2 text-[14px] tabular-nums text-ink" data-id="element-2489" data-mp="3v43griy1ubjj">
                {money(l.priceCents)} <span className="text-[12px] text-muted" data-id="element-2490" data-mp="7ef0rp4326ot">· dit merk staat geen korting toe</span>
              </p>
            </div> : <label className="block" data-id="element-2491" data-mp="g7i7zfo4xnb0">
              <span className="mb-1 block text-[12px] text-muted" data-id="element-2492" data-mp="g8jjtmcz6dts">{giftcard ? 'Waarde (€)' : 'Prijs per stuk (€)'}</span>
              <NumberInput decimals value={l.priceCents / 100} onChange={v => c.setInput(line.key, {
          priceCents: toCents(v ?? 0)
        })} className="w-28" data-id="element-2493" data-mp="l63bpp2lip47" />
            </label>}
          {l.kind === 'giftcard' && <label className="block" data-id="element-2494" data-mp="3opeg2iz9ozyw">
              <span className="mb-1 block text-[12px] text-muted" data-id="element-2495" data-mp="3tlvafux28akf">Geldig voor</span>
              <select value={l.validFor} onChange={e => c.setInput(line.key, {
          validFor: e.target.value === 'treatments' ? 'treatments' : 'all'
        })} className={inputCls} data-id="element-2496" data-mp="2ftzdmd3hgys">
                <option value="all" data-id="element-2497" data-mp="1dtz4iip1nwvp">Behandelingen en producten</option>
                <option value="treatments" data-id="element-2498" data-mp="2mrp50aq251u8">Alleen behandelingen</option>
              </select>
            </label>}
          {l.kind === 'product' && <div data-id="element-2499" data-mp="2hv77nvx245rx">
              <span className="mb-1 block text-[12px] text-muted" data-id="element-2500" data-mp="2vsvqky7kd2uc">Aantal</span>
              <Stepper label={`Aantal ${line.name}`} value={l.qty} min={1} max={MAX_QTY} onChange={qty => c.setInput(line.key, {
          qty
        })} data-id="element-2501" data-mp="2vqsqflcp5tbw" />
            </div>}
          {discounted && <label className="block min-w-[12rem] flex-1" data-id="element-2502" data-mp="35oypzwb16ndl">
              <span className="mb-1 block text-[12px] text-muted" data-id="element-2503" data-mp="30qxb7nkn1a7x">Reden korting · {money(line.listPriceCents - l.priceCents)} per stuk</span>
              <input value={l.discountReason ?? ''} onChange={e => c.setInput(line.key, {
          discountReason: e.target.value
        })} maxLength={120} placeholder="Bijv. vaste klant, proefgrootte" aria-invalid={problem ? true : undefined} className={inputCls} data-id="element-2504" data-mp="3elhkhcywujca" />
            </label>}
        </div>}
      {shortBy > 0 && <p className="mt-1.5 text-[12px] text-ink" data-id="element-2505" data-mp="by8d0hnm0bmm">
          <span className="text-clay" data-id="element-2506" data-mp="bvvw87tl87b5">Volgens de telling {level !== null && level > 0 ? `nog maar ${level} in de salon` : 'op in de salon'}.</span> Afrekenen kan gewoon; tel
          daarna even in Voorraad.
        </p>}
      {problem && <p role="alert" className="mt-1.5 text-[12px] text-clay" data-id="element-2507" data-mp="3kdi9na8qcon0">
          {problem}
        </p>}
    </li>;
}